/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useRef, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 PRO React TS examples components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Editor
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import { NoticeService } from "service/notice/service";
import { useNavigate, useParams } from "react-router-dom";

// Quill 기본 이미지 포맷은 style 속성을 제거하므로, style(width/height 등)을 유지하도록 확장
const BaseImageFormat: any = Quill.import("formats/image");
const IMAGE_ATTRS = ["alt", "height", "width", "style"];
class StyledImageFormat extends BaseImageFormat {
  static formats(domNode: any) {
    return IMAGE_ATTRS.reduce((formats: any, attr: string) => {
      if (domNode.hasAttribute(attr)) formats[attr] = domNode.getAttribute(attr);
      return formats;
    }, {});
  }
  format(name: string, value: any) {
    if (IMAGE_ATTRS.indexOf(name) > -1) {
      if (value) this.domNode.setAttribute(name, value);
      else this.domNode.removeAttribute(name);
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(StyledImageFormat, true);

function NoticeCreate(): JSX.Element {
  const params = useParams();
  const navigate = useNavigate();
  const quillRef = useRef<ReactQuill>(null);

  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  // 작성할 게시판 선택: 공지사항="A", 비밀글="E" (목록 탭의 type과 동일하게 유지)
  const [boardType, setBoardType] = useState("A");
  const [category, setCategory] = useState("공지사항");
  const [isImportant, setIsImportant] = useState("N");
  const [password, setPassword] = useState(""); // 비밀글(E)일 때만 사용

  const view = async (idx: string) => {
    try {
      const res = await NoticeService.view(idx);
      // 응답이 { success, data: {...} } 로 감싸진 경우와 top-level 둘 다 대응
      const d = res.data?.data ?? res.data ?? {};
      setTitle(d.title ?? "");
      setBoardType(d.bcode ?? "A");
      setCategory(d.category ?? "");
      setIsImportant(d.is_important ?? "N");
      // 옛 글은 줄바꿈이 문자 '\n'으로 저장돼 있어 HTML에서 렌더되지 않음 → <br>로 복원
      const html = (d.content ?? "").replace(/\r\n/g, "\n").replace(/\n/g, "<br>");
      setContent(html); // Quill은 HTML을 그대로 로드
    } catch (err) {
      console.log(err);
    }
  };

  // 에디터에 이미지 삽입 시 base64로 임베드만 함 (실제 업로드는 저장/수정 시)
  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", reader.result);
          quill.setSelection(range.index + 1, 0);
        }
      };
      reader.readAsDataURL(file);
    };
  };

  // 본문 HTML의 base64 이미지들을 S3에 업로드하고 src를 서버 조회 URL로 교체
  const uploadBase64Images = async (html: string): Promise<string> => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const images = doc.querySelectorAll('img[src^="data:"]');
    const token = localStorage.getItem("accessToken");

    for (const img of Array.from(images)) {
      const base64 = img.getAttribute("src");
      if (!base64) continue;

      const blob = await (await fetch(base64)).blob();
      const formData = new FormData();
      formData.append("image", blob, "image.png");

      const uploadRes = await fetch(`${process.env.REACT_APP_HOST}img/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await uploadRes.json();

      if (data.key) {
        img.setAttribute("src", `${process.env.REACT_APP_HOST}img/${data.key}`);
      }
    }

    return doc.body.innerHTML;
  };

  const isContentEmpty = () => {
    const plain = content.replace(/<[^>]*>/g, "").trim();
    return plain === "" && !/<img/i.test(content);
  };

  const create = async () => {
    try {
      if (title === "" || isContentEmpty()) {
        alert("내용과 제목을 입력해주세요");
        return;
      }
      if (boardType === "E" && password === "") {
        alert("비밀글은 비밀번호를 입력해주세요");
        return;
      }
      const uploadedContent = await uploadBase64Images(content);
      await NoticeService.create({
        type: boardType,
        category,
        title,
        content: uploadedContent,
        isImportant,
        password: boardType === "E" ? password : undefined,
      });
      navigate("/management/notice/notice-list");
    } catch (err) {
      console.log(err);
    }
  };

  const update = async () => {
    try {
      if (title === "" || isContentEmpty()) {
        alert("내용과 제목을 입력해주세요");
        return;
      }
      const uploadedContent = await uploadBase64Images(content);
      await NoticeService.update(
        { title, content: uploadedContent, category, isImportant },
        params.idx
      );
      navigate("/management/notice/notice-list");
    } catch (err) {
      console.log(err);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    []
  );

  useEffect(() => {
    if (params.idx) {
      view(params.idx);
    }
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Card
          style={{
            padding: "32px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {!params.idx && (
            <FormControl fullWidth>
              <InputLabel id="board-type-label">게시판</InputLabel>
              <Select
                labelId="board-type-label"
                label="게시판"
                value={boardType}
                onChange={(e: any) => {
                  const v = e.target.value;
                  setBoardType(v);
                  // 게시판에 맞춰 카테고리 자동 설정 (공지사항→공지사항, 비밀글→비밀글 고정)
                  setCategory(v === "E" ? "비밀글" : "공지사항");
                  // 비밀글은 중요 개념이 없으므로 초기화
                  if (v === "E") setIsImportant("N");
                }}
                sx={{ height: 44 }}
              >
                <MenuItem value="A">공지사항</MenuItem>
                <MenuItem value="E">비밀글</MenuItem>
              </Select>
            </FormControl>
          )}
          {boardType === "E" ? (
            <MDInput label="카테고리" value="비밀글" width="100%" disabled />
          ) : (
            <FormControl fullWidth>
              <InputLabel id="category-label">카테고리</InputLabel>
              <Select
                labelId="category-label"
                label="카테고리"
                value={category}
                onChange={(e: any) => setCategory(e.target.value)}
                sx={{ height: 44 }}
              >
                <MenuItem value="공지사항">공지사항</MenuItem>
                <MenuItem value="업데이트">업데이트</MenuItem>
              </Select>
            </FormControl>
          )}
          {boardType !== "E" && (
            <FormControlLabel
              control={
                <Switch
                  checked={isImportant === "Y"}
                  onChange={(e: any) => setIsImportant(e.target.checked ? "Y" : "N")}
                />
              }
              label="중요 게시글"
            />
          )}
          {!params.idx && boardType === "E" && (
            <MDInput
              label="비밀번호"
              type="password"
              value={password}
              width="100%"
              onChange={(e: any) => setPassword(e.target.value)}
            />
          )}
          <MDInput
            label="제목"
            value={title}
            width="100%"
            onChange={(e: any) => setTitle(e.target.value)}
          />
          <MDBox
            sx={{
              "& .ql-container": { minHeight: 300, fontSize: 14 },
              "& .ql-editor": { minHeight: 300 },
              // 모든 이미지는 에디터 폭을 넘지 않도록만 제한(레이아웃 깨짐 방지)
              "& .ql-editor img": { maxWidth: "100%" },
              // style이 없는(크기 미지정) 이미지만 세로도 제한. style 지정 이미지는 그대로 존중
              "& .ql-editor img:not([style])": { maxHeight: 400, height: "auto" },
            }}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              placeholder="공지사항 내용을 입력해주세요."
            />
          </MDBox>

          <MDBox
            width="100%"
            style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}
          >
            {params.idx ? (
              <MDButton
                color="info"
                style={{ display: "flex", flexDirection: "column" }}
                onClick={update}
              >
                {"수정하기"}
              </MDButton>
            ) : (
              <MDButton
                color="success"
                style={{ display: "flex", flexDirection: "column" }}
                onClick={create}
              >
                {"저장하기"}
              </MDButton>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default NoticeCreate;
